import { useNavigate } from '@tanstack/react-router'
import {
  useAdminOrganization,
  useAdminOrgMembers,
  useSuspendOrg,
  useUnsuspendOrg,
} from '@/hooks/useOrganizations'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { ConfirmDialog } from '@/components/admin/ConfirmDialog'
import { useState } from 'react'
import { ArrowLeft, Building2, Users, Calendar, Settings } from 'lucide-react'

interface OrganizationDetailPageProps {
  orgId: string
}

export const OrganizationDetailPage = ({ orgId }: OrganizationDetailPageProps) => {
  const navigate = useNavigate()
  const { data: org, isLoading: orgLoading } = useAdminOrganization(orgId)
  const { data: members, isLoading: membersLoading } = useAdminOrgMembers(orgId)
  const suspendOrg = useSuspendOrg()
  const unsuspendOrg = useUnsuspendOrg()
  const [showSuspend, setShowSuspend] = useState(false)
  const [showUnsuspend, setShowUnsuspend] = useState(false)

  if (orgLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-40" />
        <Skeleton className="h-64" />
      </div>
    )
  }

  if (!org) {
    return (
      <div className="space-y-4">
        <Button variant="outline" onClick={() => navigate({ to: '/organizations' })}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Volver
        </Button>
        <p className="text-muted-foreground">Organización no encontrada.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" onClick={() => navigate({ to: '/organizations' })}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Volver
        </Button>
        <div className="flex flex-1 items-center gap-3">
          <Building2 className="h-6 w-6" />
          <h1 className="text-2xl font-bold">{org.name}</h1>
          <Badge variant={org.is_suspended ? 'error' : 'default'}>
            {org.is_suspended ? 'Suspendida' : 'Activa'}
          </Badge>
        </div>
        {org.is_suspended ? (
          <Button variant="outline" onClick={() => setShowUnsuspend(true)}>
            Reactivar
          </Button>
        ) : (
          <Button variant="destructive" onClick={() => setShowSuspend(true)}>
            Suspender
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Users className="h-4 w-4" />
              Miembros
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{org.member_count}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Users className="h-4 w-4" />
              Usuarios Gestionados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{org.managed_user_count}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Calendar className="h-4 w-4" />
              Creada
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm font-bold">{new Date(org.created_at).toLocaleDateString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Settings className="h-4 w-4" />
              Crédito por miembro
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm font-bold">
              {org.settings.default_member_credit_limit ?? 'Sin límite'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Owner info */}
      <Card>
        <CardHeader>
          <CardTitle>Información del Owner</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground">Nombre</p>
            <p className="font-medium">{org.owner_name}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Email</p>
            <p className="font-medium">{org.owner_email}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Créditos propios miembros</p>
            <p className="font-medium">{org.settings.allow_members_own_credits ? 'Sí' : 'No'}</p>
          </div>
        </CardContent>
      </Card>

      {/* Members table */}
      <Card>
        <CardHeader>
          <CardTitle>Miembros</CardTitle>
        </CardHeader>
        <CardContent>
          {membersLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          ) : !members || members.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No hay miembros en esta organización.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-3 pr-4 font-medium">Nombre</th>
                    <th className="pb-3 pr-4 font-medium">Email / Username</th>
                    <th className="pb-3 pr-4 font-medium">Tipo</th>
                    <th className="pb-3 pr-4 font-medium">Rol</th>
                    <th className="pb-3 pr-4 font-medium">Estado</th>
                    <th className="pb-3 font-medium">Fecha de unión</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {members.map((member) => (
                    <tr key={member.user_id}>
                      <td className="py-3 pr-4 font-medium">{member.name}</td>
                      <td className="py-3 pr-4 text-muted-foreground">
                        {member.email ?? member.username ?? '—'}
                      </td>
                      <td className="py-3 pr-4">
                        <Badge variant="outline" className="text-xs capitalize">
                          {member.type === 'managed' ? 'Gestionado' : 'Invitado'}
                        </Badge>
                      </td>
                      <td className="py-3 pr-4">
                        <Badge variant="secondary" className="text-xs capitalize">
                          {member.role}
                        </Badge>
                      </td>
                      <td className="py-3 pr-4">
                        <Badge
                          variant={member.status === 'active' ? 'default' : 'error'}
                          className="text-xs"
                        >
                          {member.status === 'active' ? 'Activo' : 'Suspendido'}
                        </Badge>
                      </td>
                      <td className="py-3 text-muted-foreground">
                        {new Date(member.joined_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={showSuspend}
        onOpenChange={setShowSuspend}
        title="Suspender Organización"
        description={`¿Estás seguro de suspender "${org.name}"? Los usuarios no podrán acceder.`}
        confirmLabel="Suspender"
        onConfirm={async () => {
          await suspendOrg.mutateAsync(org.id)
          setShowSuspend(false)
        }}
        isLoading={suspendOrg.isPending}
      />

      <ConfirmDialog
        open={showUnsuspend}
        onOpenChange={setShowUnsuspend}
        title="Reactivar Organización"
        description={`¿Estás seguro de reactivar "${org.name}"?`}
        confirmLabel="Reactivar"
        onConfirm={async () => {
          await unsuspendOrg.mutateAsync(org.id)
          setShowUnsuspend(false)
        }}
        isLoading={unsuspendOrg.isPending}
      />
    </div>
  )
}
